import { useState, useCallback, useRef, useEffect } from 'react'
import { Send, Smile } from 'lucide-react'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import type { EmojiClickData } from 'emoji-picker-react'
import { cn } from '@/lib/utils'

/**
 * ChatInput component - message composition field.
 *
 * Features:
 * - Auto-resize textarea
 * - Send on Enter (Shift+Enter for newline)
 * - Emoji picker
 * - Character count
 */

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  maxLength?: number
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
  maxLength = 2000,
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`
    }
  }, [message])

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false)
      }
    }

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  const handleSubmit = useCallback(() => {
    const trimmed = message.trim()
    if (!trimmed || disabled) return

    onSend(trimmed)
    setMessage('')

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [message, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter without Shift
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      if (value.length <= maxLength) {
        setMessage(value)
      }
    },
    [maxLength]
  )

  const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
    setMessage((prev) => prev + emojiData.emoji)
    setShowEmojiPicker(false)
    textareaRef.current?.focus()
  }, [])

  const isOverLimit = message.length > maxLength * 0.9
  const isEmpty = message.trim().length === 0

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <div className="flex items-end gap-2">
        {/* Emoji button */}
        <div className="relative" ref={emojiPickerRef}>
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className={cn(
              'touch-target flex items-center justify-center rounded-full p-2 text-text-light hover:bg-gray-100 hover:text-primary',
              showEmojiPicker && 'bg-gray-100 text-primary'
            )}
            title="Emojis"
          >
            <Smile className="h-5 w-5" />
          </button>

          {/* Emoji picker popup */}
          {showEmojiPicker && (
            <div className="absolute bottom-full left-0 z-50 mb-2">
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                theme={Theme.LIGHT}
                width={320}
                height={400}
                searchPlaceholder="Search emoji..."
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
        </div>

        {/* Textarea wrapper */}
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              'w-full resize-none rounded-2xl border border-gray-200 px-4 py-2.5',
              'text-sm placeholder:text-text-muted',
              'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'max-h-[150px]'
            )}
          />

          {/* Character count */}
          {message.length > 0 && (
            <span
              className={cn(
                'absolute bottom-1 right-3 text-xs',
                isOverLimit ? 'text-danger' : 'text-text-muted'
              )}
            >
              {message.length}/{maxLength}
            </span>
          )}
        </div>

        {/* Send button - touch-friendly size */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || isEmpty}
          className={cn(
            'touch-target flex items-center justify-center rounded-full p-2.5',
            'transition-colors',
            isEmpty ? 'bg-gray-100 text-text-muted' : 'bg-primary text-white hover:bg-primary-dark',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          title="Send message"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>

      {/* Hint - hidden on mobile */}
      <p className="mt-2 hidden text-center text-xs text-text-muted sm:block">
        Press <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">Enter</kbd> to
        send, <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">Shift+Enter</kbd>{' '}
        for new line
      </p>
    </div>
  )
}
